import React, { useEffect, useMemo, useState } from 'react'
import { Drawer, Input, Table, Tooltip } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import DownOutlined from '@ant-design/icons/DownOutlined'
import RightOutlined from '@ant-design/icons/RightOutlined'
import ExclamationCircleFilled from '@ant-design/icons/ExclamationCircleFilled'
import { useQuery } from '@tanstack/react-query'
import { US_STATE_NAMES } from 'enrollment-platform/pages/PlanManagementV2/pages/PlanOverrides/constants'
import {
  getTaskCounties,
  isAreaZipFailed,
  type CountyAreaInfo,
  type CountyResponseDto,
} from '../api/fetch-carrier-api'
import styles from '../PlanManagementV3.module.scss'

interface Props {
  taskId: string | null
  state: string | null
  open: boolean
  onClose: () => void
}

// A county is "failed" (painted red + alert) when its scrape outcome is FAILED
// (no ZIP completed) or PARTIAL (some ZIPs failed). COMPLETED / undefined = clean.
function countyHasError(county: CountyResponseDto): boolean {
  return county.status === 'FAILED' || county.status === 'PARTIAL'
}

function countyErrorLabel(county: CountyResponseDto): string {
  return county.status === 'FAILED'
    ? 'All ZIPs failed in this county'
    : 'Some ZIPs failed in this county'
}

const areaColumns: ColumnsType<CountyAreaInfo> = [
  {
    title: 'ZIP',
    dataIndex: 'zipCode',
    key: 'zipCode',
    width: '34%',
    render: (zip: string, row) => {
      if (!isAreaZipFailed(row)) return zip
      const cell = (
        <span className={styles.zipCellError}>
          <ExclamationCircleFilled
            className={styles.alertIcon}
            aria-hidden="true"
          />
          {zip}
        </span>
      )
      if (!row.errorMessage) return cell
      return (
        <Tooltip
          title={
            <span className={styles.nestedTooltip}>{row.errorMessage}</span>
          }
        >
          {cell}
        </Tooltip>
      )
    },
  },
  {
    title: 'Rating Area',
    dataIndex: 'ratingArea',
    key: 'ratingArea',
    width: '33%',
  },
  {
    title: 'Plans',
    dataIndex: 'totalPlans',
    key: 'totalPlans',
    width: '33%',
    render: (value: number) => value.toLocaleString(),
  },
]

function ZipDetailTable({ areas }: { areas: CountyAreaInfo[] }) {
  return (
    <div className={styles.nestedAreaTable}>
      <Table<CountyAreaInfo>
        rowKey="zipCode"
        size="small"
        pagination={false}
        columns={areaColumns}
        dataSource={areas}
        rowClassName={(row) => (isAreaZipFailed(row) ? styles.errorRow : '')}
      />
    </div>
  )
}

export default function CountyBreakdownDrawer({
  taskId,
  state,
  open,
  onClose,
}: Props) {
  const [search, setSearch] = useState('')

  useEffect(() => {
    setSearch('')
  }, [taskId, state])

  const { data, isLoading, error } = useQuery({
    queryKey: ['taskCounties', taskId, state],
    queryFn: () =>
      getTaskCounties(taskId as string, { state: state as string }),
    enabled: open && !!taskId && !!state,
  })

  const counties = useMemo(
    () =>
      [...(data ?? [])].sort((a, b) =>
        a.countyName.localeCompare(b.countyName)
      ),
    [data]
  )

  const filteredCounties = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return counties
    return counties.filter(
      (county) =>
        county.countyName.toLowerCase().includes(query) ||
        county.countyCode.includes(query) ||
        county.areasInfos.some((area) => area.zipCode.includes(query))
    )
  }, [counties, search])

  const columns = useMemo<ColumnsType<CountyResponseDto>>(
    () => [
      {
        title: 'County',
        dataIndex: 'countyName',
        key: 'countyName',
        render: (value: string, row) => {
          if (!countyHasError(row)) {
            return (
              <span>
                {value}{' '}
                <span className={styles.countyCode}>· {row.countyCode}</span>
              </span>
            )
          }
          return (
            <Tooltip
              title={
                <span className={styles.nestedTooltip}>
                  {countyErrorLabel(row)}
                </span>
              }
            >
              <span className={styles.zipCellError}>
                <ExclamationCircleFilled
                  className={styles.alertIcon}
                  aria-hidden="true"
                />
                {value}{' '}
                <span className={styles.countyCode}>· {row.countyCode}</span>
              </span>
            </Tooltip>
          )
        },
      },
      {
        title: 'Zips',
        dataIndex: 'totalAreas',
        key: 'totalAreas',
        width: 90,
      },
      {
        title: 'Total Plans',
        dataIndex: 'totalPlans',
        key: 'totalPlans',
        width: 130,
        render: (value: number) => value.toLocaleString(),
      },
    ],
    []
  )

  const stateName = state ? (US_STATE_NAMES[state] ?? state) : ''

  return (
    <Drawer
      title={state ? `${stateName} (${state}) — by county` : 'By county'}
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <p className={styles.connectionDescription}>
        {counties.length} {counties.length === 1 ? 'county' : 'counties'}
      </p>

      <Input
        placeholder="Search by county or ZIP"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {error ? (
        <p className={styles.nestedError}>
          Could not load county data. Please try again.
        </p>
      ) : null}

      <div className={styles.nestedPanelCard}>
        <Table<CountyResponseDto>
          rowKey="countyCode"
          size="small"
          loading={isLoading}
          pagination={false}
          columns={columns}
          dataSource={filteredCounties}
          indentSize={0}
          locale={{ emptyText: 'No county data' }}
          rowClassName={(row) => (countyHasError(row) ? styles.errorRow : '')}
          expandable={{
            rowExpandable: (row) => row.areasInfos.length > 0,
            expandedRowRender: (row) => (
              <ZipDetailTable areas={row.areasInfos} />
            ),
            expandIcon: ({ expanded, onExpand, record, expandable }) => {
              if (!expandable) {
                return <span className={styles.expandIconSpacer} />
              }
              return (
                <button
                  type="button"
                  className={styles.expandButton}
                  aria-label={
                    expanded
                      ? `Collapse zips for ${record.countyName}`
                      : `Expand zips for ${record.countyName}`
                  }
                  aria-expanded={expanded}
                  onClick={(event) => onExpand(record, event)}
                >
                  {expanded ? (
                    <DownOutlined aria-hidden="true" />
                  ) : (
                    <RightOutlined aria-hidden="true" />
                  )}
                </button>
              )
            },
          }}
        />
      </div>
    </Drawer>
  )
}
